import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Ticket, Copy, Warning, ArrowsClockwise, Trash, DownloadSimple, ChatCircleDots, CheckCircle,
} from "@phosphor-icons/react";
import api, { apiErr } from "../api";
import { FounderShell, MetricCard } from "../components/founder";

const FEEDBACK_STATUSES = ["new", "reviewing", "planned", "resolved", "closed"];

export default function FounderBeta() {
  return (
    <FounderShell>
      <div className="space-y-5" data-testid="founder-beta-page">
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Beta Command</h1>
        <BetaAnalytics />
        <ProgressionPacing />
        <InvitePanel />
        <FeedbackPanel />
      </div>
    </FounderShell>
  );
}

function BetaAnalytics() {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/beta/analytics").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const inv = data?.invites || {};
  const ob = data?.onboarding || {};
  const co = data?.cohorts || {};
  return (
    <section className="ff-panel rounded-md p-5" data-testid="beta-analytics">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-ff-gold"><Ticket size={20} weight="duotone" /> Onboarding Analytics</h2>
        <button onClick={load} data-testid="beta-analytics-refresh" className="flex items-center gap-1 rounded-sm border border-ff-brass/30 px-2.5 py-1 text-xs text-ff-brass hover:text-ff-gold"><ArrowsClockwise size={13} /> Refresh</button>
      </div>
      {!data ? <p className="text-sm text-ff-muted">Loading…</p> : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <MetricCard label="Invites Generated" metric={inv.generated} testid="beta-inv-generated" />
          <MetricCard label="Invites Redeemed" metric={inv.redeemed} testid="beta-inv-redeemed" />
          <MetricCard label="Invite Conversion" metric={inv.conversion_pct} suffix="%" testid="beta-inv-conversion" />
          <MetricCard label="Unused Invites" metric={inv.unused} testid="beta-inv-unused" />
          <MetricCard label="Beta Players" metric={co.beta_real_players} hint="excludes preview/test" testid="beta-cohort" />
          <MetricCard label="Onboarding Start" metric={ob.start_rate_pct} suffix="%" hint={`${ob.started || 0} started`} testid="beta-ob-start" />
          <MetricCard label="Completed" metric={ob.completion_rate_pct} suffix="%" hint={`${ob.completed || 0} of ${ob.cohort_size || 0}`} testid="beta-ob-complete" />
          <MetricCard label="Skipped" metric={ob.skip_rate_pct} suffix="%" hint={`${ob.skipped || 0} skipped`} testid="beta-ob-skip" />
        </div>
      )}
    </section>
  );
}

function ProgressionPacing() {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/founder/progression").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const mv = (m) => (m == null ? "—" : typeof m === "object" ? (m.status ? "—" : m.value) : m);
  const src = data?.crew_xp_by_source || {};
  const flagged = data?.flagged_fast_l10 || {};
  return (
    <section className="ff-panel rounded-md p-5" data-testid="progression-pacing">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-ff-gold"><Ticket size={20} weight="duotone" /> Crew Progression Pacing <span className="text-[11px] font-normal text-ff-muted">({data?.curve_version || "…"})</span></h2>
        <button onClick={load} data-testid="pacing-refresh" className="flex items-center gap-1 rounded-sm border border-ff-brass/30 px-2.5 py-1 text-xs text-ff-brass hover:text-ff-gold"><ArrowsClockwise size={13} /> Refresh</button>
      </div>
      {!data ? <p className="text-sm text-ff-muted">Loading…</p> : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <MetricCard label="Cohort (real players)" metric={data.cohort_size} testid="pacing-cohort" />
            <MetricCard label="Reach L5" metric={data.pct_players_reaching?.["5"]} suffix="%" testid="pacing-l5" />
            <MetricCard label="Reach L7" metric={data.pct_players_reaching?.["7"]} suffix="%" testid="pacing-l7" />
            <MetricCard label="Reach L9" metric={data.pct_players_reaching?.["9"]} suffix="%" testid="pacing-l9" />
            <MetricCard label="Reach L10" metric={data.pct_players_reaching?.["10"]} suffix="%" testid="pacing-l10" />
            <MetricCard label="XP: base" metric={src.base} testid="pacing-xp-base" />
            <MetricCard label="XP: contributor" metric={src.contributor_bonus} testid="pacing-xp-contrib" />
            <MetricCard label="XP: practice" metric={src.practice} hint="policy: always 0" testid="pacing-xp-practice" />
            <MetricCard label="Dupes prevented" metric={data.duplicate_awards_prevented} testid="pacing-dupes" />
            <MetricCard label="Fastest → L10 (days)" metric={data.fastest_legit_progression_to_l10_days} testid="pacing-fastest" />
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <div className="rounded-sm border border-ff-brass/20 bg-ff-bg/40 p-3">
              <div className="mb-1 text-[11px] uppercase tracking-widest text-ff-brass">Median expeditions to level</div>
              <div className="flex flex-wrap gap-2 text-xs text-ff-parchment" data-testid="pacing-median-exp">
                {["3", "5", "7", "9", "10"].map((lv) => (
                  <span key={lv} className="rounded-sm bg-ff-bg px-2 py-1">L{lv}: <b className="text-ff-gold">{mv(data.median_expeditions_to_level?.[lv])}</b></span>
                ))}
              </div>
            </div>
            <div className="rounded-sm border border-ff-brass/20 bg-ff-bg/40 p-3">
              <div className="mb-1 text-[11px] uppercase tracking-widest text-ff-brass">Progression version</div>
              <div className="flex flex-wrap gap-2 text-xs text-ff-parchment" data-testid="pacing-version-dist">
                {Object.entries(data.progression_version_distribution || {}).map(([v, n]) => (
                  <span key={v} className="rounded-sm bg-ff-bg px-2 py-1">{v}: <b className="text-ff-gold">{n}</b></span>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-3 rounded-sm border border-ff-gold/25 bg-ff-gold/5 p-3" data-testid="pacing-flagged">
            <div className="flex items-center gap-2 text-xs text-ff-gold">
              <Warning size={14} weight="fill" /> Fast L10 flags ({"<"}{flagged.threshold_days}d): <b>{flagged.count ?? 0}</b>
              <span className="text-ff-muted">— review only, no automatic action</span>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function InvitePanel() {
  const [count, setCount] = useState(5);
  const [expires, setExpires] = useState(30);
  const [note, setNote] = useState("");
  const [founderOnlyNote, setFounderOnlyNote] = useState(false);
  const [multiUse, setMultiUse] = useState(false);
  const [busy, setBusy] = useState(false);
  const [freshCodes, setFreshCodes] = useState(null); // plaintext shown ONCE
  const [list, setList] = useState(null);

  const load = useCallback(() => {
    api.get("/beta/invites").then((r) => setList(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/beta/invites/generate", {
        count: Number(count), expires_days: Number(expires),
        note: note.trim() || null, founder_only_note: founderOnlyNote,
        multi_use: multiUse,
      });
      setFreshCodes(data.codes);
      setNote("");
      load();
      toast.success(
        multiUse
          ? "Shareable code generated — one code, unlimited testers. Copy it now."
          : `${data.codes.length} invite(s) generated — copy them now.`
      );
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const copyAll = () => {
    if (!freshCodes) return;
    navigator.clipboard?.writeText(freshCodes.join("\n")).then(
      () => toast.success("Codes copied to clipboard"),
      () => toast.error("Copy failed — select and copy manually")
    );
  };

  const revoke = async (code_hash) => {
    try {
      await api.post("/beta/invites/revoke", { code_hash });
      toast.success("Invite revoked");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/beta/invites/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "aurumward-invites.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const counts = list?.counts || {};
  const STATUS_COLOR = {
    unused: "text-emerald-300", redeemed: "text-ff-gold", revoked: "text-red-300", expired: "text-ff-muted",
  };

  return (
    <section className="ff-panel rounded-md p-5" data-testid="invite-panel">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-ff-gold"><Ticket size={20} weight="duotone" /> Invite Codes</h2>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <label className="text-xs text-ff-brass">Count
          <input type="number" min={1} max={100} value={multiUse ? 1 : count} disabled={multiUse} onChange={(e) => setCount(e.target.value)} data-testid="invite-count"
            className="mt-1 w-full rounded-sm border border-ff-brass/30 bg-ff-bg p-2 text-sm text-ff-parchment disabled:opacity-50" />
        </label>
        <label className="text-xs text-ff-brass">Expires (days)
          <input type="number" min={0} max={365} value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="invite-expires"
            className="mt-1 w-full rounded-sm border border-ff-brass/30 bg-ff-bg p-2 text-sm text-ff-parchment" />
        </label>
        <label className="col-span-2 text-xs text-ff-brass">Note (optional)
          <input type="text" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} data-testid="invite-note" placeholder="e.g. Reddit wave 1"
            className="mt-1 w-full rounded-sm border border-ff-brass/30 bg-ff-bg p-2 text-sm text-ff-parchment" />
        </label>
      </div>
      <label className="mt-2 flex items-center gap-2 text-xs text-ff-muted" data-testid="invite-founder-note-toggle">
        <input type="checkbox" checked={founderOnlyNote} onChange={() => setFounderOnlyNote((v) => !v)} className="h-4 w-4 accent-ff-gold" />
        Keep this note founder-only (hidden from ordinary admins)
      </label>
      <label className="mt-2 flex items-start gap-2 text-xs text-ff-muted" data-testid="invite-multiuse-toggle">
        <input type="checkbox" checked={multiUse} onChange={() => setMultiUse((v) => !v)} className="mt-0.5 h-4 w-4 accent-ff-gold" />
        <span>
          <b className="text-ff-brass">Shareable code</b> — create one code that many testers can use (great for friends & family). Otherwise each code works once.
        </span>
      </label>

      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={generate} disabled={busy} data-testid="invite-generate-btn"
          className="rounded-sm bg-ff-gold px-4 py-2 text-sm font-semibold text-ff-bg hover:bg-ff-brass disabled:opacity-60">
          {busy ? "Generating…" : (multiUse ? "Generate Shareable Code" : "Generate Invites")}
        </button>
        <button onClick={exportCsv} data-testid="invite-export-btn"
          className="flex items-center gap-1.5 rounded-sm border border-ff-brass/30 px-4 py-2 text-sm text-ff-brass hover:text-ff-gold">
          <DownloadSimple size={15} /> Export CSV
        </button>
      </div>

      {freshCodes && (
        <div className="mt-4 rounded-sm border-2 border-ff-gold/50 bg-ff-gold/10 p-4" data-testid="fresh-codes">
          <div className="mb-2 flex items-start gap-2 text-xs text-ff-gold">
            <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
            <span><b>Copy or save these now.</b> Codes are hashed at rest and can never be shown again. Closing this box loses the plaintext.</span>
          </div>
          <div className="space-y-1 font-mono-ff text-sm text-ff-parchment">
            {freshCodes.map((c) => <div key={c} data-testid="fresh-code" className="select-all">{c}</div>)}
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={copyAll} data-testid="copy-codes-btn" className="flex items-center gap-1.5 rounded-sm bg-ff-gold px-3 py-1.5 text-xs font-semibold text-ff-bg hover:bg-ff-brass"><Copy size={14} /> Copy all</button>
            <button onClick={() => setFreshCodes(null)} data-testid="dismiss-codes-btn" className="rounded-sm border border-ff-brass/30 px-3 py-1.5 text-xs text-ff-muted hover:text-ff-gold">I've saved them</button>
          </div>
        </div>
      )}

      {list && (
        <div className="mt-5">
          <div className="mb-2 flex flex-wrap gap-3 text-xs text-ff-muted" data-testid="invite-counts">
            <span>Total: <b className="text-ff-parchment">{list.total}</b></span>
            <span className="text-emerald-300">Unused: {counts.unused || 0}</span>
            <span className="text-ff-gold">Redeemed: {counts.redeemed || 0}</span>
            <span className="text-red-300">Revoked: {counts.revoked || 0}</span>
            <span>Expired: {counts.expired || 0}</span>
          </div>
          <div className="max-h-80 overflow-y-auto rounded-sm border border-ff-brass/20">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-ff-bg2 text-ff-brass">
                <tr><th className="p-2">Code handle</th><th className="p-2">Status</th><th className="p-2">Created</th><th className="p-2">Expires</th><th className="p-2"></th></tr>
              </thead>
              <tbody data-testid="invite-list">
                {list.codes.map((c) => (
                  <tr key={c.code_hash} className="border-t border-ff-brass/10" data-testid={`invite-row-${c.code_hash.slice(0, 8)}`}>
                    <td className="p-2 font-mono-ff text-ff-muted" title={c.code_hash}>{c.code_hash.slice(0, 12)}…</td>
                    <td className={`p-2 font-medium ${STATUS_COLOR[c.status] || "text-ff-muted"}`}>
                      {c.status}
                      {c.multi_use && <span className="ml-1 rounded-sm bg-ff-gold/15 px-1.5 py-0.5 text-[10px] font-semibold text-ff-gold" data-testid="multiuse-badge">shareable</span>}
                    </td>
                    <td className="p-2 text-ff-muted">{(c.created_at || "").slice(0, 10)}</td>
                    <td className="p-2 text-ff-muted">{c.expires_at ? c.expires_at.slice(0, 10) : "—"}</td>
                    <td className="p-2 text-right">
                      {c.status === "unused" && (
                        <button onClick={() => revoke(c.code_hash)} data-testid={`revoke-${c.code_hash.slice(0, 8)}`} className="inline-flex items-center gap-1 rounded-sm border border-red-500/40 px-2 py-1 text-[11px] text-red-300 hover:bg-red-500/10"><Trash size={12} /> Revoke</button>
                      )}
                    </td>
                  </tr>
                ))}
                {list.codes.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-ff-muted">No invites yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

function FeedbackPanel() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => {
    const q = filter ? `?status=${filter}` : "";
    api.get(`/beta/feedback${q}`).then((r) => setItems(r.data.feedback || [])).catch((e) => toast.error(apiErr(e)));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (fid, status) => {
    try {
      await api.post(`/beta/feedback/${fid}/status`, { status });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <section className="ff-panel rounded-md p-5" data-testid="feedback-panel">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-ff-gold"><ChatCircleDots size={20} weight="duotone" /> Player Feedback</h2>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="feedback-filter" className="rounded-sm border border-ff-brass/30 bg-ff-bg p-1.5 text-xs text-ff-parchment">
          <option value="">All</option>
          {FEEDBACK_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {!items ? <p className="text-sm text-ff-muted">Loading…</p> : items.length === 0 ? (
        <p className="text-sm text-ff-muted">No feedback yet.</p>
      ) : (
        <div className="space-y-3" data-testid="feedback-list">
          {items.map((f) => (
            <div key={f.id} className="rounded-sm border border-ff-brass/20 bg-ff-bg/40 p-3" data-testid={`feedback-item-${f.id.slice(0, 8)}`}>
              <div className="flex flex-wrap items-center gap-2 text-[11px]">
                <span className="rounded-full bg-ff-gold/15 px-2 py-0.5 font-medium text-ff-gold">{f.category}</span>
                <span className="rounded-full border border-ff-brass/25 px-2 py-0.5 text-ff-muted">{f.sentiment}</span>
                {f.route && <span className="font-mono-ff text-ff-muted">{f.route}</span>}
                <span className="text-ff-muted">{(f.created_at || "").slice(0, 16).replace("T", " ")}</span>
                {f.may_contact && <span className="flex items-center gap-0.5 text-emerald-300"><CheckCircle size={12} /> may contact</span>}
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-ff-parchment">{f.message}</p>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {FEEDBACK_STATUSES.map((s) => (
                  <button key={s} onClick={() => setStatus(f.id, s)} data-testid={`fb-status-${f.id.slice(0, 8)}-${s}`}
                    className={`rounded-sm border px-2 py-0.5 text-[10px] ${f.status === s ? "border-ff-gold bg-ff-gold/10 text-ff-gold" : "border-ff-brass/25 text-ff-muted hover:text-ff-parchment"}`}>{s}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
